import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { FaFilePdf, FaTimes, FaUpload } from 'react-icons/fa';

export interface ICancellationData {
    note: string;
    file: File | null;
}

interface ICancellationModal {
    show: boolean;
    title: string;
    onHide: () => void;
    onConfirm: (data: ICancellationData) => void;
    isLoading?: boolean;
}

export function CancellationModal({ show, title, onHide, onConfirm, isLoading = false }: ICancellationModal) {
    const [note, setNote] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            setNote('');
            setFile(null);
            setError('');
        }
    }, [show]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: 'application/pdf',
        multiple: false,
        onDropAccepted: files => {
            setError('');
            setFile(files[0]);
        },
        onDropRejected: () => setError('Only PDF files are allowed.'),
    });

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group className="mb-4">
                    <Form.Label>Cancellation PDF (optional)</Form.Label>
                    <div
                        {...getRootProps()}
                        style={{ borderStyle: 'dashed' }}
                        className={`border rounded p-5 text-center cursor-pointer ${
                            isDragActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle'
                        }`}
                    >
                        <input {...getInputProps()} />
                        <FaUpload className="text-muted mb-2" size={22} />
                        <div className="fs-7 text-muted">Drag and drop a PDF here, or click to select</div>
                    </div>
                    {error && <div className="text-danger fs-8 mt-2">{error}</div>}
                    {file && (
                        <div className="d-flex align-items-center justify-content-between border rounded p-2 mt-3">
                            <span className="d-flex align-items-center gap-2 text-truncate">
                                <FaFilePdf className="text-danger" />
                                <span className="text-truncate">{file.name}</span>
                            </span>
                            <Button variant="" size="sm" onClick={() => setFile(null)} aria-label="Remove file">
                                <FaTimes />
                            </Button>
                        </div>
                    )}
                </Form.Group>

                <Form.Group>
                    <Form.Label>Note (optional)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={note}
                        onChange={event => setNote(event.target.value)}
                        placeholder="Reason or details about the cancellation"
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide} disabled={isLoading}>
                    Close
                </Button>
                <Button variant="primary" onClick={() => onConfirm({ note, file })} disabled={isLoading}>
                    Confirm cancellation
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
